import React, { useState } from "react";
import { useGeofence } from "@/contexts/GeofenceContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, MapPin, RefreshCw, LogOut, Navigation, AlertTriangle, Loader2, Crosshair, ShieldOff, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function GuardGeofenceBlock() {
    const { status, distanceMeters, societyConfig, checkLocation, checking, errorMessage, coords, refreshConfig } = useGeofence();
    const { user, logout } = useAuth();
    const [calibrating, setCalibrating] = useState(false);

    const canCalibrate = Boolean(user?.role === "admin" || user?.email === "neighbornest.in@gmail.com");

    const formatDistance = (meters) => {
        if (meters == null) return "Unknown distance";
        if (meters < 1000) return `${meters} meters`;
        return `${(meters / 1000).toFixed(1)} km`;
    };

    const handleCalibrateHere = async () => {
        if (!coords?.latitude || !coords?.longitude) {
            toast.error("GPS position not acquired. Please enable location first.");
            return;
        }
        setCalibrating(true);
        try {
            await api.post("/society/geofence", {
                society_name: societyConfig.society_name,
                full_name: societyConfig.full_name,
                address: societyConfig.address,
                latitude: coords.latitude,
                longitude: coords.longitude,
                radius_meters: Math.max(Number(societyConfig.radius_meters) || 1800, 1800),
                guard_strict: true,
                resident_warning: true,
                enabled: true
            });
            toast.success("Success! Pyramid City 3 calibrated to your exact current GPS position.");
            await refreshConfig();
            checkLocation();
        } catch (err) {
            toast.error("Could not save calibration coordinates.");
        } finally {
            setCalibrating(false);
        }
    };

    const handleDisableGeofence = async () => {
        setCalibrating(true);
        try {
            await api.post("/society/geofence", {
                ...societyConfig,
                guard_strict: false
            });
            toast.success("Guard geofence disabled! Guards can now access without location restrictions.");
            await refreshConfig();
            checkLocation();
        } catch (err) {
            toast.error("Failed to update setting");
        } finally {
            setCalibrating(false);
        }
    };

    return (
        <div className="min-h-[85vh] flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-3xl border-2 border-red-200 shadow-2xl p-6 sm:p-8 text-center space-y-6 animate-scale-up">
                {/* Icon header */}
                <div className="relative mx-auto w-20 h-20 rounded-3xl bg-red-50 border-2 border-red-200 flex items-center justify-center shadow-inner">
                    <ShieldAlert size={40} className="text-red-600 animate-pulse" />
                    <div className="absolute -bottom-1 -right-1 bg-red-600 text-white p-1 rounded-full shadow">
                        <MapPin size={14} />
                    </div>
                </div>

                {/* Main Heading & Message */}
                <div className="space-y-2">
                    <span className="text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-red-100 text-red-800 border border-red-200 inline-block">
                        Location Restriction Active
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-display font-black text-neutral-900 tracking-tight">
                        You are not in {societyConfig.society_name}
                    </h2>
                    <p className="text-sm text-neutral-600 max-w-md mx-auto leading-relaxed">
                        Access to the Guard Gatekeeper portal is strictly restricted to{" "}
                        <span className="font-semibold text-neutral-800">{societyConfig.society_name}</span>. Gatekeeper actions can only be performed when physically present on duty.
                    </p>
                </div>

                {/* Status / Distance Card */}
                <div className="bg-neutral-50 rounded-2xl border border-neutral-200 p-4 text-left space-y-2.5">
                    <div className="flex items-center justify-between text-xs pb-2 border-b border-neutral-200">
                        <span className="text-neutral-500 font-medium">Assigned Location:</span>
                        <span className="font-bold text-neutral-900 text-right">{societyConfig.address}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs pb-2 border-b border-neutral-200">
                        <span className="text-neutral-500 font-medium">Society Perimeter:</span>
                        <span className="font-mono font-bold text-neutral-800">Within {societyConfig.radius_meters}m</span>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                        <span className="text-neutral-500 font-medium">Your Current Distance:</span>
                        {status === "checking" ? (
                            <span className="flex items-center gap-1.5 text-nest-clay font-bold font-mono">
                                <Loader2 size={12} className="animate-spin" />
                                Checking GPS…
                            </span>
                        ) : status === "denied" ? (
                            <span className="font-bold text-red-600 flex items-center gap-1">
                                <AlertTriangle size={12} />
                                Permission Denied
                            </span>
                        ) : status === "unavailable" ? (
                            <span className="font-bold text-amber-700">GPS Signal Unavailable</span>
                        ) : (
                            <span className="font-mono font-black text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md">
                                ~{formatDistance(distanceMeters)} away
                            </span>
                        )}
                    </div>
                </div>

                {/* If Permission Denied, show clear iPhone / Safari Enable Guide */}
                {status === "denied" && (
                    <div className="bg-amber-50/90 rounded-2xl border-2 border-amber-300 p-4 text-left text-xs space-y-2.5">
                        <div className="flex items-center gap-2 text-amber-950 font-bold text-sm">
                            <Smartphone size={16} className="text-amber-700" />
                            <span>How to Allow Location on iPhone (Safari):</span>
                        </div>
                        <ol className="list-decimal list-inside space-y-1.5 text-amber-900 text-[11px] leading-relaxed">
                            <li>Open iPhone <strong>Settings ⚙️</strong> ➔ <strong>Privacy & Security</strong> ➔ <strong>Location Services</strong> (ensure turned <strong>ON</strong>).</li>
                            <li>Scroll down in that list and tap <strong>Safari Websites</strong> (or your browser).</li>
                            <li>Select <strong>"While Using the App"</strong> and toggle ON <strong>"Precise Location"</strong>.</li>
                            <li>Come back here and tap <strong>"Re-check My Location"</strong> below.</li>
                        </ol>
                        <div className="pt-1 border-t border-amber-200/80 text-[11px] text-amber-800">
                            <strong>Quick shortcut in Safari:</strong> Tap the <strong>aA</strong> icon in the top/bottom address bar ➔ <strong>Website Settings</strong> ➔ <strong>Location</strong> ➔ change from <em>Deny</em> to <strong>Allow</strong>.
                        </div>
                    </div>
                )}

                {errorMessage && status !== "denied" && (
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 text-left flex items-start gap-2">
                        <AlertTriangle size={15} className="shrink-0 text-amber-600 mt-0.5" />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-2 pt-2">
                    {/* Admin 1-Tap Calibration Button if sitting in society */}
                    {canCalibrate && coords && (
                        <div className="p-3.5 bg-emerald-50 border-2 border-emerald-300 rounded-2xl text-left space-y-2 mb-2">
                            <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-950">
                                <Crosshair size={14} className="text-emerald-700" />
                                <span>Are you sitting at Pyramid City 3 right now?</span>
                            </div>
                            <p className="text-[11px] text-emerald-800 leading-snug">
                                As Admin, tap below to set your exact GPS coordinates as the official society center (0 km).
                            </p>
                            <button
                                type="button"
                                disabled={calibrating}
                                onClick={handleCalibrateHere}
                                className="w-full py-2.5 px-3 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-95 transition"
                            >
                                {calibrating ? <Loader2 size={13} className="animate-spin" /> : <Crosshair size={13} />}
                                <span>Set My Current Spot as Pyramid City 3 (Calibrate)</span>
                            </button>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={checkLocation}
                        disabled={checking}
                        className="w-full py-3.5 px-4 rounded-2xl bg-nest-ink hover:bg-neutral-800 text-white font-bold text-sm transition cursor-pointer shadow-lg shadow-neutral-900/15 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
                    >
                        {checking ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                <span>Verifying GPS Location…</span>
                            </>
                        ) : (
                            <>
                                <RefreshCw size={16} />
                                <span>Re-check My Location</span>
                            </>
                        )}
                    </button>

                    {/* Admin Emergency Toggle */}
                    {canCalibrate && (
                        <button
                            type="button"
                            disabled={calibrating}
                            onClick={handleDisableGeofence}
                            className="w-full py-2.5 px-3 rounded-2xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold text-xs transition cursor-pointer flex items-center justify-center gap-1.5"
                            title="Turn off location restriction for guards"
                        >
                            <ShieldOff size={14} />
                            <span>Admin Override: Turn OFF Guard Geofence</span>
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={logout}
                        className="w-full py-3 px-4 rounded-2xl border border-neutral-300 hover:bg-neutral-100 text-neutral-700 font-semibold text-xs transition cursor-pointer flex items-center justify-center gap-1.5"
                    >
                        <LogOut size={14} />
                        <span>Logout / Switch User</span>
                    </button>
                </div>

                <p className="text-[11px] text-neutral-400">
                    If you are physically at Pyramid City 3 and seeing this message, please ensure device Location / GPS is turned ON and tap "Re-check My Location".
                </p>
            </div>
        </div>
    );
}

